import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Chip, Button, Grid } from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import client from '../api/client';

const FEATURE_LABELS = {
  lead_generation: "Lead Generation Bot", customer_support: "Customer Support Bot",
  live_chat: "Live Chat", canned_responses: "Canned Responses",
  analytics: "Chatbot Analytics", template_message_analytics: "Template Message Analytics",
  webhook_integrations: "Webhook Integrations", google_sheets: "Google Sheets Integration",
  crm_standard: "CRM (Standard)", email_otp: "Email OTP Validations",
  lead_notifications: "Lead Email Notifications", hot_lead_notifications: "Hot Lead Notifications",
  auto_email_followup: "Auto Email Followup", multilingual: "Multi-Lingual (2 Languages)",
  ai_bot: "AI Bot (Self Setup)", ai_speech: "AI Speech Answering",
  voice_intent_mapping: "Voice Enabled Intent Mapping", csv_export: "CSV Chats Export",
  contacts_dashboard: "Contacts Dashboard", whatsapp_catalog: "WhatsApp Catalog",
  whatsapp_ecommerce: "WhatsApp E-Commerce", "2fa": "2 Factor Authentication",
  lead_data_masking: "Lead Data Masking",
};

const LIMIT_ITEMS = [
  { key: 'chatbots', label: 'Chatbots' },
  { key: 'chatsPerMonth', label: 'Chats / Month' },
  { key: 'waUserChatsPerMonth', label: 'WhatsApp Chats / Month' },
  { key: 'liveAgents', label: 'Live Agents' },
  { key: 'languages', label: 'Languages' },
  { key: 'aiMessages', label: 'AI Messages' },
  { key: 'maxDocuments', label: 'Documents' },
  { key: 'maxHelpArticles', label: 'Help Articles' },
];

const formatLimit = (v) => (v === -1 || v === undefined || v === null ? 'Unlimited' : v.toLocaleString('en-IN'));

const formatPrice = (p) => (p === undefined || p === null ? '—' : `₹${Number(p).toLocaleString('en-IN')}`);

const YourPlan = ({ account }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get('/api/plans')
      .then(res => setPlans(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>;
  }

  const plan = plans.find(p => p.name === account?.planName) || null;

  if (!plan) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <WorkspacePremiumIcon sx={{ fontSize: 44, color: '#94a3b8', mb: 1.5 }} />
        <Typography fontWeight={700} fontSize={17} color="#1e293b" mb={0.5}>No active plan</Typography>
        <Typography variant="body2" color="#64748b" mb={2.5}>Contact Talktokam admin to assign a plan to your account.</Typography>
        <Button variant="contained" onClick={() => window.alert('Contact Talktokam admin to upgrade your plan.')}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#075e54', '&:hover': { bgcolor: '#054d44' } }}>
          Contact Admin
        </Button>
      </Box>
    );
  }

  const limits = plan.limits || {};

  return (
    <Box>
      {/* Plan hero */}
      <Paper elevation={0} sx={{
        p: 3, borderRadius: 3, mb: 3, position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(135deg, #075e54, #0a8a7a)', color: '#fff',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <WorkspacePremiumIcon sx={{ fontSize: 26 }} />
          <Typography fontSize={22} fontWeight={800}>{plan.name}</Typography>
          {plan.isEnterprise && (
            <Chip size="small" label="Enterprise" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: 11 }} />
          )}
        </Box>
        <Typography fontSize={30} fontWeight={900}>{formatPrice(plan.price)}</Typography>
        <Typography fontSize={12.5} color="rgba(255,255,255,0.75)" mt={0.5}>Billed per month · {plan.slug}</Typography>
      </Paper>

      {/* Limits */}
      <Typography fontWeight={700} fontSize={15} color="#1e293b" mb={1.5}>Plan Limits</Typography>
      <Grid container spacing={2} mb={3.5}>
        {LIMIT_ITEMS.map(item => (
          <Grid item xs={6} sm={3} key={item.key}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #eef2f7', textAlign: 'center' }}>
              <Typography fontSize={17} fontWeight={800} color="#075e54">{formatLimit(limits[item.key])}</Typography>
              <Typography fontSize={10.5} color="#94a3b8" fontWeight={600}>{item.label}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Features */}
      <Typography fontWeight={700} fontSize={15} color="#1e293b" mb={1.5}>Included Features</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {(plan.features || []).map((f, i) => (
          <Chip key={i} icon={<CheckCircleIcon sx={{ fontSize: 15, color: '#075e54 !important' }} />}
            label={FEATURE_LABELS[f] || f}
            sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600, fontSize: 12, borderRadius: 1.5 }} />
        ))}
        {(plan.features || []).length === 0 && (
          <Typography variant="body2" color="#94a3b8">No features listed</Typography>
        )}
      </Box>

      {/* Upgrade */}
      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={() => window.alert('Contact Talktokam admin to upgrade your plan.')}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#075e54', '&:hover': { bgcolor: '#054d44' }, px: 3 }}>
          Upgrade Plan
        </Button>
        <Typography variant="caption" color="#94a3b8">Need a custom plan? Contact Talktokam admin.</Typography>
      </Box>
    </Box>
  );
};

export default YourPlan;
